// Rebuilds the Getting Started steps from Strapi into the nested shape the Help Hub and the new Home render: each step
// carries its help page's fields, and `guide` / `sections` / `leadSections` / `guides` rows may hold a `page` of their
// own, with the same fields, down as many levels as the content goes.
//
// Strapi stores every nested help page as its own GetStartedPage entry and links to it by relation; the steps and
// the component rows only carry that link. Here the links are swapped back for the pages themselves.

// The component fields, by the name both Strapi and the constants use for them.
const COMPONENT_KEYS = ['guide', 'sections', 'leadSections', 'guides', 'steps', 'tasks']

// Strapi's bookkeeping, and the fields that only exist because Strapi can't hold the constants' shape.
const DROPPED_KEYS = new Set(['id', 'createdAt', 'updatedAt', 'publishedAt', 'order', 'hideHelpAction', 'linkText', 'linkHref'])

// Strapi answers an unset field with null, where the constants leave the key out — and a Vue prop default only
// applies to undefined, so a null `list` would reach the template instead of [].
const isUnset = (value) => value === null || value === '' || (Array.isArray(value) && !value.length)

// The Media Library serves relative paths; the API host carries the origin they resolve against.
const mediaUrl = (media) => {
  const url = media?.data?.attributes?.url

  if (!url) return undefined

  return url.startsWith('/') ? `${new URL(import.meta.env.VITE_STRAPI_API_HOST).origin}${url}` : url
}

const buildEntry = (attributes, pageFor) => {
  const entry = {}

  for (const [key, value] of Object.entries(attributes)) {
    if (DROPPED_KEYS.has(key) || isUnset(value)) continue

    if (key === 'videoImage') entry.videoImage = mediaUrl(value)
    else if (key === 'page') entry.page = pageFor(value?.data?.attributes?.slug)
    else if (COMPONENT_KEYS.includes(key)) entry[key] = value.map((row) => buildEntry(row, pageFor))
    else entry[key] = value
  }

  // One link, flattened in Strapi to two text fields.
  if (attributes.linkText || attributes.linkHref) entry.link = { text: attributes.linkText, href: attributes.linkHref }

  // `helpRoute: false` hides the step page's action; Strapi can't put false in a text field.
  if (attributes.hideHelpAction) entry.helpRoute = false

  // An unlinked or unpublished page leaves its row as plain text rather than a link to nowhere.
  if ('page' in entry && !entry.page) delete entry.page

  return entry
}

// `steps` and `pages` are the `data` arrays of /app-help-hub-pages and /get-started-pages.
export const getStartedStepsFromStrapi = (steps, pages) => {
  const pagesBySlug = Object.fromEntries(pages.map(({ attributes }) => [attributes.slug, attributes]))
  const built = {}

  // Pages are built on first use and kept, so a page linked from two places is one object. The object is registered
  // before it is filled, so two pages an editor links to each other can't recurse forever.
  const pageFor = (slug) => {
    if (!slug || !pagesBySlug[slug]) return undefined

    if (!built[slug]) {
      built[slug] = {}
      Object.assign(built[slug], buildEntry(pagesBySlug[slug], pageFor))
    }

    return built[slug]
  }

  return [...steps]
    .sort((a, b) => a.attributes.order - b.attributes.order)
    .map(({ attributes }) => buildEntry(attributes, pageFor))
}
